// Payment on the Review & pay step: Stripe's Payment Element (cards, plus
// Apple Pay / Google Pay where the browser and Stripe account allow them)
// for the PaymentIntent the checkout created. On the local copy of the site
// there is no Stripe key and the local backend only pretends to take
// payments (~/feislink-dev/local), so a "Simulate payment" button stands in.
import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { Icon } from "./parts";

const publishKey = process.env.REACT_APP_PUBLISHKEY;
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise && publishKey) stripePromise = loadStripe(publishKey);
  return stripePromise;
};

/** True only on a local copy: no Stripe key and the API on this machine. */
export const localPayments =
  !publishKey && /^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?\/?$/.test(process.env.REACT_APP_BASE_URL || "");

// The payment this form was opened for can't be confirmed any more (Stripe
// only says "A processing error occurred."). What happened to it decides
// what the parent sees:
//   canceled: another tab or device opened Review & pay for the same family
//     and feis, which replaces it (one open payment per family and feis);
//   succeeded: it was paid elsewhere (the server reuses a payment only for
//     the same selection and charge, so it paid for what's on this screen);
//   anything else (processing...): it may still complete, so never offer
//     another payment for it.
export const settledElsewhere = (err) => {
  const status = err?.payment_intent?.status;
  if (status === "canceled") return "replaced";
  if (status === "succeeded") return "paid";
  if (err?.code === "payment_intent_unexpected_state") return "pending";
  return null;
};

const PayButton = ({ label, returnUrl, onBeforePay, onReplaced, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pay = async () => {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError("");
    onBeforePay();
    const { error: failed } = await stripe.confirmPayment({ elements, confirmParams: { return_url: returnUrl } });
    // Success leaves this page for return_url; we only get here on an error.
    const settled = failed && settledElsewhere(failed);
    if (settled === "replaced" && onReplaced) onReplaced();
    else if (settled === "paid" && onPaid) onPaid();
    else if (settled === "pending") {
      setError("This payment is still being processed. Check your entries on the dashboard in a few minutes before paying again.");
    } else if (failed) setError(failed.message || "The payment didn't go through. Please try again.");
    setBusy(false);
  };
  return (
    <>
      {error && (
        <div className="rg-note error" style={{ marginTop: 12 }}><Icon name="error" /><div>{error}</div></div>
      )}
      <button type="button" className="rg-cta" style={{ marginTop: 14 }} disabled={!stripe || busy} onClick={pay}>
        <Icon name="lock" />
        {busy ? "Paying…" : label}
      </button>
    </>
  );
};

export const PayPanel = ({ clientSecret, intentId, label, returnUrl, onBeforePay, onSimulated, onReplaced, onPaid }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const options = useMemo(() => ({ clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#68ad3f", fontFamily: "Rubik, sans-serif", borderRadius: "8px" } } }), [clientSecret]);

  if (localPayments) {
    const simulate = async () => {
      setBusy(true);
      setError("");
      try {
        onBeforePay();
        await serviceConsumer("POST", `${process.env.REACT_APP_BASE_URL}/__local/pay/${intentId}`, {});
        onSimulated(intentId);
      } catch (e) {
        setError(e?.messages || "The local payment simulation failed.");
        setBusy(false);
      }
    };
    return (
      <div className="rg-pay">
        <h4>Payment</h4>
        <div className="rg-note amber">
          <Icon name="science" />
          <div>Local copy: no real card form here. This marks the payment as paid, as Stripe would.</div>
        </div>
        {error && <div className="rg-note error"><Icon name="error" /><div>{error}</div></div>}
        <button type="button" className="rg-cta" disabled={busy} onClick={simulate}>
          <Icon name="lock" />
          {busy ? "Paying…" : `Simulate payment · ${label.replace(/^Pay\s*/, "")}`}
        </button>
      </div>
    );
  }

  const stripe = getStripe();
  if (!stripe || !clientSecret) {
    return (
      <div className="rg-pay">
        <div className="rg-note error"><Icon name="error" /><div>Payments aren't available right now. Please try again later.</div></div>
      </div>
    );
  }
  return (
    <div className="rg-pay">
      <h4>Payment</h4>
      <Elements stripe={stripe} options={options} key={clientSecret}>
        <PaymentElement options={{ layout: "tabs" }} />
        <PayButton label={label} returnUrl={returnUrl} onBeforePay={onBeforePay} onReplaced={onReplaced} onPaid={onPaid} />
      </Elements>
      <div className="rg-secure"><Icon name="lock" />Payments processed securely by Stripe</div>
    </div>
  );
};
